import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ProductGrid } from '../ui/ProductGrid';
import { useCategories } from '../core/hooks/useCategories';
import { useProducts } from '../core/hooks/useProducts';
import { useBrands } from '../core/hooks/useBrands';
import { useSets } from '../core/hooks/useSets';
import { Search, Filter, Grid, List, ShoppingCart } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loading } from '../../components/ui/Loading';
import AddItemPopup from '../../components/AddItemPopup';
import { useCart } from '../core/cart/CartContext';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Initialize state from URL params immediately to avoid race conditions
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [brandId, setBrandId] = useState<string | undefined>(() => searchParams.get('brand') || undefined);
  const [setId, setSetId] = useState<string | undefined>(undefined);
  const [onSale, setOnSale] = useState<boolean>(() => searchParams.get('onSale') === 'true');
  const [orderBy, setOrderBy] = useState<'date' | 'price' | 'name'>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const { popupOpen, popupMessage, hidePopup } = useCart();
  
  const { categories, loading: catsLoading, fetchCategories } = useCategories();
  const { brands, loading: brandsLoading, refetch: fetchBrands } = useBrands();
  const { sets, loading: setsLoading, refetch: fetchSets } = useSets();
  
  // Use useProducts directly with all filters
  const { products, loading, error, fetchProducts } = useProducts({
    perPage: 100, // Fetch all products at once (no pagination)
    page: 1,
    category: categoryId,
    brand: brandId,
    set: setId,
    search: searchTerm,
    onSale: onSale,
    orderBy: orderBy,
    order: order
  });
  
  const catMenuRef = useRef<HTMLDivElement | null>(null);
  const [isCatMenuOpen, setIsCatMenuOpen] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Record<number, boolean>>({});
  const brandMenuRef = useRef<HTMLDivElement | null>(null);
  const [isBrandMenuOpen, setIsBrandMenuOpen] = useState(false);
  const setMenuRef = useRef<HTMLDivElement | null>(null);
  const [isSetMenuOpen, setIsSetMenuOpen] = useState(false);

  // Load categories, brands, and sets on mount
  useEffect(() => {
    fetchCategories({ forceRefresh: true } as any);
    fetchBrands();
    fetchSets();
  }, [fetchCategories, fetchBrands, fetchSets]);

  // Sync URL params to state - only runs when URL changes (e.g., from navbar navigation)
  // This effect should NOT have state values in its dependency array
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const brandParam = searchParams.get('brand');
    const setParam = searchParams.get('set');
    const onSaleParam = searchParams.get('onSale');
    const searchParam = searchParams.get('search');
    
    // Update all filter states from URL
    setSearchTerm(searchParam || '');
    setOnSale(onSaleParam === 'true');
    setBrandId(brandParam || undefined);
    setSetId(setParam || undefined);
    
    // Handle category - need to wait for categories to load to resolve name -> id
    if (categoryParam && !catsLoading && categories.length > 0) {
      // Try to find by name first
      const category = categories.find(cat => 
        cat.name.toLowerCase() === categoryParam.toLowerCase()
      );
      if (category) {
        setCategoryId(category.id);
      } else {
        // Try as numeric ID
        const parsedId = parseInt(categoryParam);
        if (!isNaN(parsedId)) {
          setCategoryId(parsedId);
        } else {
          setCategoryId(undefined);
        }
      }
    } else if (!categoryParam) {
      setCategoryId(undefined);
    }
    // Note: if categoryParam exists but categories are still loading, 
    // categoryId will be set once categories finish loading (next effect run)
  }, [searchParams, categories, catsLoading]);

  // Fetch products when filter state changes
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    
    // Wait for categories to load if there's a category in URL
    if (categoryParam && (catsLoading || categories.length === 0)) {
      return;
    }
    
    // If URL has category param but we haven't resolved it to an ID yet, wait
    if (categoryParam && categoryId === undefined) {
      return;
    }
    
    fetchProducts({
      perPage: 100,
      page: 1,
      category: categoryId,
      brand: brandId,
      set: setId,
      search: searchTerm,
      onSale: onSale,
      orderBy: orderBy,
      order: order
    });
  }, [categoryId, brandId, setId, searchTerm, onSale, orderBy, order, fetchProducts, catsLoading, categories.length, searchParams]);

  // Close category menu on outside click
  useEffect(() => {
    if (!isCatMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (catMenuRef.current && !catMenuRef.current.contains(target)) {
        setIsCatMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isCatMenuOpen]);

  // Close brand menu on outside click
  useEffect(() => {
    if (!isBrandMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (brandMenuRef.current && !brandMenuRef.current.contains(target)) {
        setIsBrandMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isBrandMenuOpen]);

  // Close set menu on outside click
  useEffect(() => {
    if (!isSetMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (setMenuRef.current && !setMenuRef.current.contains(target)) {
        setIsSetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isSetMenuOpen]);

  return (
    <div className="shop-page min-h-screen bg-primary text-tertiary">
      <Helmet>
        <title>Shop Pokemon & One Piece Cards Online | Oracle Gaming South Africa</title>
        <meta name="description" content="Shop the latest Pokemon cards, One Piece cards, Yu-Gi-Oh, and Magic: The Gathering online. Free shipping over R1899. South Africa's premier TCG store." />
        <meta name="keywords" content="Pokemon cards online, One Piece cards online, TCG shop South Africa, Pokemon booster packs, One Piece booster boxes, Yu-Gi-Oh cards, Magic The Gathering cards, trading cards online, TCG singles, card accessories" />
        <meta property="og:title" content="Shop Pokemon & One Piece Cards Online | Oracle Gaming South Africa" />
        <meta property="og:description" content="Shop the latest Pokemon cards, One Piece cards, Yu-Gi-Oh, and Magic: The Gathering online. Free shipping over R1899. South Africa's premier TCG store." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oracle-gaming.vercel.app/shop" />
        <meta property="og:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shop Pokemon & One Piece Cards Online | Oracle Gaming South Africa" />
        <meta name="twitter:description" content="Shop the latest Pokemon cards, One Piece cards, Yu-Gi-Oh, and Magic: The Gathering online. Free shipping over R1899." />
        <meta name="twitter:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <link rel="canonical" href="https://oracle-gaming.vercel.app/shop" />
      </Helmet>
      <Header />
      <div className="h-20" />
      <section className="gallery-carousel" style={{paddingTop: '120px', paddingBottom: '30px'}}>
        <div className="container">
          <h1 className="h2 section-title" style={{marginBottom: '40px'}}>OUR SHOP</h1>
          <div className="btn-group" style={{justifyContent: 'center', gap: '16px'}}>
              <div className="relative" style={{minWidth: 320, maxWidth: 520, width: '60%'}}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={18} color="#14fefb" />
                <input
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    const params: Record<string, string> = {};
                    if (categoryId) params.category = categoryId.toString();
                    if (brandId) params.brand = brandId;
                    if (setId) params.set = setId;
                    if (onSale) params.onSale = 'true';
                    if (value) params.search = value;
                    setSearchParams(Object.keys(params).length > 0 ? params : {});
                  }}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-3"
                  style={{
                    background: 'var(--raisin-black-1)',
                    color: 'var(--white)',
                    border: '1px solid rgba(20, 254, 251, 0.4)'
                  }}
                />
              </div>
            </div>
            <Link to="/cart" className="btn btn-primary" style={{display:'inline-flex'}}>
              <ShoppingCart className="w-5 h-5" />
              <span>Cart</span>
            </Link>
          </div>
        </div>
      </section>
      {/* Filters and Controls */}
      <section className="gears" style={{paddingTop: 0}}>
        <div className="container" style={{paddingInline: 15}}>
          <div className="flex justify-center" style={{padding: '16px 0'}}>
            <div className="flex flex-wrap items-center gap-4 justify-center">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5" color="#14fefb" />
                <span style={{color:'var(--white)'}}>Filters:</span>
              </div>
              {/* Brands dropdown styled like categories */}
              <div ref={brandMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setIsBrandMenuOpen(prev => !prev)}
                  disabled={brandsLoading}
                  style={{
                    background:'var(--raisin-black-1)',
                    color:'var(--white)',
                    border:'1px solid rgba(20, 254, 251, 0.4)',
                    padding:'8px 12px',
                    width: '240px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {brandsLoading ? 'Loading brands...' : (brandId || 'All Brands')}
                  </span>
                  <span style={{ fontSize: 18, padding: '4px 8px' }}>▾</span>
                </button>
                {isBrandMenuOpen && !brandsLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      minWidth: '220px',
                      background: 'var(--raisin-black-1, #1f1f1f)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '6px 0',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                      maxHeight: '320px',
                      overflowY: 'auto'
                    }}
                  >
                    <div
                      onClick={() => {
                        setBrandId(undefined);
                        setIsBrandMenuOpen(false);
                        const params: Record<string, string> = {};
                        if (categoryId) params.category = categoryId.toString();
                        if (setId) params.set = setId;
                        if (onSale) params.onSale = 'true';
                        if (searchTerm) params.search = searchTerm;
                        setSearchParams(Object.keys(params).length > 0 ? params : {});
                      }}
                      style={{ padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      All Brands
                    </div>
                    {brands.map(brand => (
                      <div
                        key={brand.id}
                        onClick={() => {
                          const val = brand.name;
                          setBrandId(val);
                          setIsBrandMenuOpen(false);
                          const params: Record<string, string> = {};
                          if (categoryId) params.category = categoryId.toString();
                          if (val) params.brand = val;
                          if (setId) params.set = setId;
                          if (onSale) params.onSale = 'true';
                          if (searchTerm) params.search = searchTerm;
                          setSearchParams(Object.keys(params).length > 0 ? params : {});
                        }}
                        style={{ padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {brand.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Single dropdown with collapsible groups */}
              <div ref={catMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setIsCatMenuOpen(prev => !prev)}
                  className=""
                  disabled={catsLoading}
                  style={{
                    background:'var(--raisin-black-1)',
                    color:'var(--white)',
                    border:'1px solid rgba(20, 254, 251, 0.4)',
                    padding:'8px 12px',
                    width: '240px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{catsLoading ? 'Loading categories...' : (categoryId ? (categories.find(c => c.id === categoryId)?.name || 'Category') : 'All Categories')}</span>
                  <span style={{ fontSize: 18, padding: '4px 8px' }}>▾</span>
                </button>
                {isCatMenuOpen && !catsLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      minWidth: '280px',
                      background: 'var(--raisin-black-1, #1f1f1f)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '6px 0',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                      maxHeight: '360px',
                      overflowY: 'auto'
                    }}
                  >
                    <div
                      onClick={() => {
                        setCategoryId(undefined);
                        setIsCatMenuOpen(false);
                        const params: Record<string, string> = {};
                        if (brandId) params.brand = brandId;
                        if (setId) params.set = setId;
                        if (onSale) params.onSale = 'true';
                        if (searchTerm) params.search = searchTerm;
                        setSearchParams(Object.keys(params).length > 0 ? params : {});
                      }}
                      style={{ padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      All Categories
                    </div>
                    {categories
                      .filter(cat => !cat.parent || cat.parent === 0)
                      .map(parent => {
                        const isExpanded = !!expandedParents[parent.id];
                        const toggle = (e?: React.MouseEvent) => { if (e) e.stopPropagation(); setExpandedParents(prev => ({ ...prev, [parent.id]: !prev[parent.id] })); };
                        return (
                          <div key={`gp-${parent.id}`} style={{}}
                          onClick={() => {
                            setCategoryId(parent.id);
                            setIsCatMenuOpen(false);
                            const params: Record<string, string> = { category: parent.id.toString() };
                            if (brandId) params.brand = brandId;
                            if (setId) params.set = setId;
                            if (onSale) params.onSale = 'true';
                            if (searchTerm) params.search = searchTerm;
                            setSearchParams(params);
                          }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', cursor: 'pointer' }}>
                              <span>{parent.name}</span>
                              <button
                                type="button"
                                onClick={toggle}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}
                              >
                                {isExpanded ? '▾' : '▸'}
                              </button>
                            </div>
                            {isExpanded && (
                              <div style={{ padding: '4px 0' }}>
                                {categories
                                  .filter(child => child.parent === parent.id)
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(child => (
                                    <div
                                      key={`gc-${child.id}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCategoryId(child.id);
                                        setIsCatMenuOpen(false);
                                        const params: Record<string, string> = { category: child.id.toString() };
                                        if (brandId) params.brand = brandId;
                                        if (setId) params.set = setId;
                                        if (onSale) params.onSale = 'true';
                                        if (searchTerm) params.search = searchTerm;
                                        setSearchParams(params);
                                      }}
                                      style={{ padding: '6px 22px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                      {child.name}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="onSale"
                  checked={onSale}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setOnSale(checked);
                    const params: Record<string, string> = {};
                    if (categoryId) {
                      params.category = categoryId.toString();
                    }
                    if (brandId) {
                      params.brand = brandId;
                    }
                    if (setId) {
                      params.set = setId;
                    }
                    if (checked) {
                      params.onSale = 'true';
                    }
                    if (searchTerm) {
                      params.search = searchTerm;
                    }
                    setSearchParams(Object.keys(params).length > 0 ? params : {});
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="onSale" className="text-sm font-medium" style={{color:'var(--platinum)'}}>
                  On Sale
                </label>
              </div>
              <select 
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value as 'date' | 'price' | 'name')} 
                style={{
                  background:'var(--raisin-black-1)',
                  color:'var(--white)',
                  border:'1px solid rgba(20, 254, 251, 0.4)',
                  padding:'8px 12px',
                  height: '40px'
                }}
              >
                <option value="date">Newest</option>
                <option value="price">Price</option>
                <option value="name">Name</option>
              </select>
              <select 
                value={order}
                onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')} 
                style={{
                  background:'var(--raisin-black-1)',
                  color:'var(--white)',
                  border:'1px solid rgba(20, 254, 251, 0.4)',
                  padding:'8px 12px',
                  height: '40px'
                }}
              >
                <option value="desc">High to Low</option>
                <option value="asc">Low to High</option>
              </select>
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1" style={{ background: 'var(--raisin-black-1)', borderColor: 'var(--orange)' }}>
                <button 
                  onClick={() => setViewMode('grid')} 
                  className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                  style={{ 
                    background: viewMode === 'grid' ? 'var(--orange)' : 'transparent',
                    color: viewMode === 'grid' ? 'var(--raisin-black-1)' : 'var(--platinum)'
                  }}
                  title="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewMode('list')} 
                  className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                  style={{ 
                    background: viewMode === 'list' ? 'var(--orange)' : 'transparent',
                    color: viewMode === 'list' ? 'var(--raisin-black-1)' : 'var(--platinum)'
                  }}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Set Filter - Below horizontal filters, mobile responsive */}
          <div className="flex justify-center" style={{padding: '16px 0', paddingTop: '8px'}}>
            <div className="flex flex-wrap items-center gap-4 justify-center w-full max-w-4xl">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5" color="#14fefb" />
                <span style={{color:'var(--white)'}}>Set:</span>
              </div>
              <div ref={setMenuRef} style={{ position: 'relative', width: '100%', maxWidth: '240px' }}>
                <button
                  type="button"
                  onClick={() => setIsSetMenuOpen(prev => !prev)}
                  disabled={setsLoading}
                  style={{
                    background:'var(--raisin-black-1)',
                    color:'var(--white)',
                    border:'1px solid rgba(20, 254, 251, 0.4)',
                    padding:'8px 12px',
                    width: '100%',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {setsLoading ? 'Loading sets...' : (setId || 'All Sets')}
                  </span>
                  <span style={{ fontSize: 18, padding: '4px 8px' }}>▾</span>
                </button>
                {isSetMenuOpen && !setsLoading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      minWidth: '220px',
                      background: 'var(--raisin-black-1, #1f1f1f)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '6px 0',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                      maxHeight: '320px',
                      overflowY: 'auto'
                    }}
                  >
                    <div
                      onClick={() => {
                        setSetId(undefined);
                        setIsSetMenuOpen(false);
                        const params: Record<string, string> = {};
                        if (categoryId) params.category = categoryId.toString();
                        if (brandId) params.brand = brandId;
                        if (onSale) params.onSale = 'true';
                        if (searchTerm) params.search = searchTerm;
                        setSearchParams(Object.keys(params).length > 0 ? params : {});
                      }}
                      style={{ padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      All Sets
                    </div>
                    {sets.map(set => (
                      <div
                        key={set.id}
                        onClick={() => {
                          const val = set.name;
                          setSetId(val);
                          setIsSetMenuOpen(false);
                          const params: Record<string, string> = {};
                          if (categoryId) params.category = categoryId.toString();
                          if (brandId) params.brand = brandId;
                          if (val) params.set = val;
                          if (onSale) params.onSale = 'true';
                          if (searchTerm) params.search = searchTerm;
                          setSearchParams(Object.keys(params).length > 0 ? params : {});
                        }}
                        style={{ padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {set.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="gears" id="gears">
        <div className="container mx-auto px-4">
          <h2 className="h2 section-title">Shop TCG and Merch</h2>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loading size="lg" text="Loading products..." />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-400 mb-4">Error loading products: {error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="btn btn-primary"
              >
                Retry
              </button>
            </div>
          ) : (
            <ProductGrid
              products={products}
              loading={loading}
              error={error}
              variant={'grid'}
              columns={4}
              onViewDetails={(p) => navigate(`/shop/product/${p.id}`)}
              className="gears-list"
            />
          )}
        </div>
      </section>
      <Footer />
      <AddItemPopup isOpen={popupOpen} message={popupMessage} onClose={hidePopup} />
    </div>
    
  );
}



